import React, { useEffect, useState } from 'react'
import log from 'loglevel'
import { EditTextOnClick } from 'src/components/Reusable/Inputs/EditTextOnClick'
import { Risk } from '@trustero/trustero-api-web/lib/risk/risk_pb'
import { FieldConstants } from 'src/Utils/globalConstants'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { FlexAlign, FlexRow } from 'src/components/Reusable/Flex'
import { InfoTooltip } from 'src/components/Reusable/Tooltip/InfoTooltip'
import { useCreateOrUpdateRisks } from '../risks.hooks'
import {
  FirstLabel,
  RiskFieldSection,
  RiskOverviewContainer,
  RiskTextValue,
  RisksCaption,
} from '../risks.styles'
import { RiskFields } from '../risks.constants'
import { RisksMathMenu } from '../menus'
import { updatedRiskRpcData } from '../risks.helpers'
import { CalculatedRiskMathChip } from '../risks.components'

export const RiskOverview = ({ risk }: { risk: Risk }): JSX.Element => {
  const createOrUpdateRisk = useCreateOrUpdateRisks()
  const riskObj = risk instanceof Risk ? risk.toObject() : undefined
  const canUpdateRisk = useCanPerformAction<Risk.AsObject>(
    CAPABILITY.CAN_UPDATE_RISKS,
    riskObj,
  )
  const [preCondition, setPreCondition] = useState<string>(
    risk.getPredisposingCondition(),
  )
  const [assetAtRisk, setAssetAtRisk] = useState<string>(risk.getAssetAtRisk())
  const [impact, setImpact] = useState<string>(risk.getImpactToCustomer())

  useEffect(() => {
    setPreCondition(risk.getPredisposingCondition())
    setAssetAtRisk(risk.getAssetAtRisk())
    setImpact(risk.getImpactToCustomer())
  }, [risk])

  const riskId = risk.getId()
  const adverseImpact = risk.getAdverseImpact()
  const likelihood = risk.getLikelihood()

  const onSave = async (newValue: string, field: RiskFields): Promise<void> => {
    try {
      const updatedRisk = updatedRiskRpcData(risk, field, newValue)
      await createOrUpdateRisk([updatedRisk])
      switch (field) {
        case RiskFields.predisposingCondition:
          setPreCondition(newValue)
          break
        case RiskFields.assetAtRisk:
          setAssetAtRisk(newValue)
          break
        case RiskFields.impactToCustomer:
          setImpact(newValue)
          break
        default:
          break
      }
    } catch (err) {
      log.error(`Failed to update Risk ${field}: `, err)
    }
  }

  return (
    <RiskOverviewContainer>
      {/* Predisposing Condition */}
      <FlexRow>
        <FirstLabel>Predisposing condition (vulnerability)</FirstLabel>
        <InfoTooltip
          id="predisposing_condition"
          body="Give context to the risk such as known design flaws or a condition that makes potential impact bigger."
        />
      </FlexRow>
      <EditTextOnClick
        isInline
        initialValue={preCondition}
        onSave={(newValue: string) =>
          onSave(newValue, RiskFields.predisposingCondition)
        }
        onCancel={() => setPreCondition(risk.getPredisposingCondition())}
        isEmpty={!preCondition}
        isDisabled={!canUpdateRisk}
      >
        <RiskTextValue>{preCondition || FieldConstants.NONE}</RiskTextValue>
      </EditTextOnClick>

      {/* Asset at Risk */}
      <FlexRow>
        <FirstLabel>Asset at risk</FirstLabel>
      </FlexRow>
      <EditTextOnClick
        isInline
        initialValue={assetAtRisk}
        onSave={(newValue: string) => onSave(newValue, RiskFields.assetAtRisk)}
        onCancel={() => setAssetAtRisk(risk.getAssetAtRisk())}
        isEmpty={!assetAtRisk}
        isDisabled={!canUpdateRisk}
      >
        <RiskTextValue>{assetAtRisk || FieldConstants.NONE}</RiskTextValue>
      </EditTextOnClick>

      {/* Impact to Customer */}
      <FlexRow>
        <FirstLabel>Impact to customer</FirstLabel>
        <InfoTooltip
          id="impact"
          body="What does this scenario mean for a customer, if anything? Try to be concrete."
        />
      </FlexRow>
      <EditTextOnClick
        isInline
        initialValue={impact}
        onSave={(newValue: string) =>
          onSave(newValue, RiskFields.impactToCustomer)
        }
        onCancel={() => setImpact(risk.getImpactToCustomer())}
        isEmpty={!impact}
        isDisabled={!canUpdateRisk}
      >
        <RiskTextValue>{impact || FieldConstants.NONE}</RiskTextValue>
      </EditTextOnClick>

      {/* Inherent Risk */}
      <FlexRow>
        <FirstLabel>Inherent risk</FirstLabel>
        <InfoTooltip
          id="risk_calculation"
          body="Use this section to partially quantify the probability and impact of your risks."
        />
      </FlexRow>
      <FlexRow
        justify={FlexAlign.FLEX_START}
        align={FlexAlign.CENTER}
        gap={12}
        $mt={8}
        isFullWidth
      >
        <RiskFieldSection>
          <FirstLabel>Likelihood of occurrence</FirstLabel>
          <RisksMathMenu riskId={riskId} field={RiskFields.likelihood} />
        </RiskFieldSection>
        <RiskFieldSection>
          <FirstLabel>Impact</FirstLabel>
          <RisksMathMenu riskId={riskId} field={RiskFields.adverseImpact} />
        </RiskFieldSection>
        <RiskFieldSection>
          <FirstLabel>
            Inherent risk
            <InfoTooltip
              id="inherent_risk"
              body="This is calculated based on likelihood and impact, using NIST guidelines."
            />
          </FirstLabel>
          <CalculatedRiskMathChip
            riskId={riskId}
            impact={adverseImpact}
            likelihood={likelihood}
            field={RiskFields.inherentRisk}
            showValue={true}
            $height="40px"
          />
        </RiskFieldSection>
      </FlexRow>
      <RisksCaption>
        *The inherent risk is determined by what are selected for “Likelihood of
        occurrence” and “Impact.”
      </RisksCaption>
    </RiskOverviewContainer>
  )
}
